import { useEffect, useRef, useState, useMemo } from 'react'
import { select } from 'd3-selection'
import 'd3-transition'
import { scaleTime, scaleLinear } from 'd3-scale'
import { line, curveMonotoneX, curveLinear, curveStep, curveBasis } from 'd3-shape'
import { axisBottom, axisLeft } from 'd3-axis'
import { extent, max } from 'd3-array'
import { timeFormat } from 'd3-time-format'
import { useChartDimensions } from './useChartDimensions'
import { useChartColors } from './useChartColors'
import { ChartTooltip } from './ChartTooltip'
import { ChartLegend } from './ChartLegend'
import type { Series, ChartConfig, ThresholdLine } from './types'

const CURVES = {
  monotone: curveMonotoneX,
  linear: curveLinear,
  step: curveStep,
  basis: curveBasis,
} as const

export interface LineChartProps extends ChartConfig {
  data: Series[]
  curveType?: keyof typeof CURVES
  showGrid?: boolean
  showDots?: boolean
  showLegend?: boolean
  thresholds?: ThresholdLine[]
  xLabel?: string
  yLabel?: string
  yDomain?: [number, number]
  formatX?: (d: Date) => string
  formatY?: (d: number) => string
}

export function LineChart({
  data,
  curveType = 'monotone',
  showGrid = true,
  showDots = false,
  showLegend = true,
  thresholds,
  xLabel,
  yLabel,
  yDomain,
  formatX,
  formatY,
  height: propHeight,
  margin: propMargin,
  animate = true,
  className = '',
}: LineChartProps) {
  const [containerRef, { width, height: autoHeight }] = useChartDimensions(600, propHeight ?? 300)
  const svgRef = useRef<SVGSVGElement>(null)
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; content: React.ReactNode }>({
    visible: false, x: 0, y: 0, content: null,
  })

  const height = propHeight ?? autoHeight
  const margin = propMargin ?? { top: 20, right: 20, bottom: 40, left: 50 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  const colorScale = useChartColors(data.map((s) => s.label))

  const allDates = useMemo(
    () => data.flatMap((s) => s.values.map((v) => new Date(v.date))),
    [data]
  )
  const allValues = useMemo(
    () => data.flatMap((s) => s.values.map((v) => v.value)),
    [data]
  )

  useEffect(() => {
    if (!svgRef.current || innerWidth <= 0 || innerHeight <= 0) return

    const svg = select(svgRef.current)
    svg.selectAll('*').remove()

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Scales
    const xExtent = extent(allDates) as [Date, Date]
    const xScale = scaleTime().domain(xExtent).range([0, innerWidth])

    const yMax = yDomain?.[1] ?? (max(allValues) ?? 100) * 1.1
    const yMin = yDomain?.[0] ?? 0
    const yScale = scaleLinear().domain([yMin, yMax]).range([innerHeight, 0]).nice()

    // Grid
    if (showGrid) {
      g.append('g')
        .attr('class', 'grid')
        .selectAll('line')
        .data(yScale.ticks(5))
        .join('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', (d) => yScale(d))
        .attr('y2', (d) => yScale(d))
        .attr('stroke', 'var(--border)')
        .attr('stroke-dasharray', '2,4')
        .attr('opacity', 0.5)
    }

    // Axes
    const xAxisGen = axisBottom(xScale).ticks(6)
    if (formatX) xAxisGen.tickFormat((d) => formatX(d as Date))
    else xAxisGen.tickFormat((d) => timeFormat('%b %d')(d as Date))

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxisGen)
      .selectAll('text')
      .attr('fill', 'var(--muted-foreground)')
      .attr('font-size', '11px')
      .attr('font-family', 'var(--font-family-body)')

    g.append('g')
      .call(axisLeft(yScale).ticks(5).tickFormat((d) => formatY ? formatY(d as number) : String(d)))
      .selectAll('text')
      .attr('fill', 'var(--muted-foreground)')
      .attr('font-size', '11px')
      .attr('font-family', 'var(--font-family-body)')

    // Remove domain lines
    g.selectAll('.domain').attr('stroke', 'var(--border)')
    g.selectAll('.tick line').attr('stroke', 'var(--border)')

    // Axis labels
    if (xLabel) {
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + 35)
        .attr('text-anchor', 'middle')
        .attr('fill', 'var(--muted-foreground)')
        .attr('font-size', '12px')
        .attr('font-family', 'var(--font-family-body)')
        .text(xLabel)
    }
    if (yLabel) {
      g.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -40)
        .attr('text-anchor', 'middle')
        .attr('fill', 'var(--muted-foreground)')
        .attr('font-size', '12px')
        .attr('font-family', 'var(--font-family-body)')
        .text(yLabel)
    }

    // Threshold lines
    if (thresholds) {
      for (const threshold of thresholds) {
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerWidth)
          .attr('y1', yScale(threshold.value))
          .attr('y2', yScale(threshold.value))
          .attr('stroke', threshold.color)
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', threshold.dashed !== false ? '6,4' : 'none')

        if (threshold.label) {
          g.append('text')
            .attr('x', innerWidth - 4)
            .attr('y', yScale(threshold.value) - 4)
            .attr('text-anchor', 'end')
            .attr('fill', threshold.color)
            .attr('font-size', '10px')
            .attr('font-family', 'var(--font-family-body)')
            .text(threshold.label)
        }
      }
    }

    // Line generator
    const curveFunc = CURVES[curveType]
    const lineGen = line<{ date: Date; value: number }>()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value))
      .curve(curveFunc)

    // Draw lines
    for (const series of data) {
      const color = series.color ?? colorScale(series.label)
      const points = series.values.map((v) => ({ date: new Date(v.date), value: v.value }))

      const path = g.append('path')
        .datum(points)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('d', lineGen)

      if (animate) {
        const totalLength = (path.node() as SVGPathElement)?.getTotalLength?.() ?? 0
        if (totalLength > 0) {
          path
            .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
            .attr('stroke-dashoffset', totalLength)
            .transition()
            .duration(800)
            .attr('stroke-dashoffset', 0)
        }
      }

      // Dots
      if (showDots) {
        g.selectAll(`.dot-${series.label}`)
          .data(points)
          .join('circle')
          .attr('cx', (d) => xScale(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', color)
          .attr('stroke', 'var(--surface)')
          .attr('stroke-width', 1.5)
      }
    }

    // Hover overlay
    const overlay = g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .attr('cursor', 'crosshair')

    overlay
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = [event.offsetX - margin.left]
        const date = xScale.invert(mx)
        const items = data.map((s) => {
          const closest = s.values.reduce((prev, curr) =>
            Math.abs(new Date(curr.date).getTime() - date.getTime()) <
            Math.abs(new Date(prev.date).getTime() - date.getTime())
              ? curr : prev
          )
          return { label: s.label, value: closest.value, color: s.color ?? colorScale(s.label) }
        })

        setTooltip({
          visible: true,
          x: event.offsetX,
          y: event.offsetY,
          content: (
            <div className="space-y-1">
              <div className="font-medium text-[var(--foreground)]">
                {timeFormat('%b %d, %Y')(date)}
              </div>
              {items.map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-[var(--muted-foreground)]">{item.label}:</span>
                  <span className="font-medium">{formatY ? formatY(item.value) : item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ),
        })
      })
      .on('mouseleave', () => setTooltip({ visible: false, x: 0, y: 0, content: null }))
  }, [data, width, height, margin, innerWidth, innerHeight, curveType, showGrid, showDots, thresholds, animate, colorScale, allDates, allValues, yDomain, formatX, formatY, xLabel, yLabel])

  const legendItems = data.map((s) => ({
    label: s.label,
    color: s.color ?? colorScale(s.label),
  }))

  return (
    <div ref={containerRef} className={`relative w-full ${className}`} style={{ minHeight: height }}>
      <svg ref={svgRef} width={width} height={height} />
      <ChartTooltip
        content={tooltip.content}
        visible={tooltip.visible}
        x={tooltip.x}
        y={tooltip.y}
        containerRef={containerRef}
      />
      {showLegend && data.length > 1 && <ChartLegend items={legendItems} className="mt-2" />}
    </div>
  )
}
